import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  MarketplaceItemStockType,
  MarketplaceItemMappingAttribute,
  MarketplaceItemStatus,
  MarketplaceItemExtended,
  MarketplaceExtendedQuery,
  MarketplaceBasketCustomFieldModel,
  CardTokenQuery,
  MemberQuery,
  AttributeValue,
  ActionLabels,
  TermsAndCondtionsType,
  TermsAndCondtion,
  MARKETPLACE_STATE,
  TEXT_COLOR,
  LoyaltyCategoryType,
} from '@fgb/core';
import { map, takeUntil, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { MarketplaceActionsComponent } from './marketplace-actions/marketplace-actions.component';

@Component({
  selector: 'fgbcl-marketplace-details',
  templateUrl: './marketplace-details.component.html',
  styleUrls: ['./marketplace-details.component.scss'],
})
export class MarketplaceDetailsComponent implements OnInit, OnDestroy {
  @Input() itemId: number;
  @Input() actionLabels = ActionLabels;
  @Input() completeRedirect: string = '/rewards/marketplace';

  @ViewChild(MarketplaceActionsComponent) marketplaceActionsComponent: MarketplaceActionsComponent;

  screenSize = ScreenType;

  marketplaceItem$: Observable<MarketplaceItemExtended | undefined>;
  marketplaceItem: MarketplaceItemExtended;
  originalStatus: MarketplaceItemStatus;
  marketplaceItemType: { backLink: string; type: string; group: string };
  theme: string;
  cardTokenCount: number = 0;
  termsAccepted = false;
  termsLink: string;
  TermsAndCondtionsType = TermsAndCondtionsType;
  termsAndCondtions: TermsAndCondtion;

  availableStock: number;
  stockCount: number;
  loading = false;
  currentLcid: number;
  isOwnWindow: boolean = false;

  attributeMappingID: number | undefined;
  selectedAttributes: AttributeValue[] | undefined;
  loyaltyCategoryType = LoyaltyCategoryType.Marketplace;

  hasSumbittedForm = false;
  validCustomFields: MarketplaceBasketCustomFieldModel[] | undefined = [];
  stockAvailableState: string;
  textColor: string;
  MARKETPLACE_STATE = MARKETPLACE_STATE;
  private _destroyed$ = new Subject<void>();

  constructor(
    private marketplaceExtendedQuery: MarketplaceExtendedQuery,
    private cardTokenQuery: CardTokenQuery,
    private memberQuery: MemberQuery,
    private router: Router
  ) {}

  ngOnInit() {
    this.cardTokenQuery
      .selectUnfrozenCards()
      .pipe(tap((tokens) => (this.cardTokenCount = tokens.length)))
      .toPromise();
    this._subToUserChange();
  }

  ngOnChanges(): void {
    this.marketplaceItem$ = this.marketplaceExtendedQuery.selectExtendedMarketplaceItem(this.itemId).pipe(
      map((item) => (item = this.marketplaceExtendedQuery.sortAttributeMappingsByValueOrdinals(item))),
      map((item) => (item = this.marketplaceExtendedQuery.setAttributeDisplayNameByLocale(item, this.currentLcid))),
      tap((item) => {
        if (item) {
          this._setUpLocalVariables(item);
          this._updateTheme(item);
          this._setUpTermsAndConditions(item);
          this.isOwnWindow = item.IsOwnWindow;
          if (this.marketplaceActionsComponent) {
            this.marketplaceActionsComponent.updatePurchaseData();
          }
          this.getStockAvailableState();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  submitForm() {
    this.hasSumbittedForm = true;
  }

  onAttributeValueChange(item: MarketplaceItemExtended, attributeSelection: MarketplaceItemMappingAttribute) {
    if (!attributeSelection || !attributeSelection.AttributeValues) {
      //trigger to disable marketplace actions if attr isn't saved
      this.marketplaceActionsComponent && this.marketplaceActionsComponent.updatePurchaseData(-999, -999);
      return;
    }
    this.attributeMappingID = attributeSelection.AttributeMappingId ? attributeSelection.AttributeMappingId : undefined;
    this.selectedAttributes = this.marketplaceExtendedQuery.getSelectedAttributes(
      item.Id,
      this.currentLcid,
      attributeSelection.AttributeValues
    );
    const useMappings =
      item.StockType === MarketplaceItemStockType.AttributeStockType &&
      item.AttributeMappings &&
      item.AttributeMappings.length > 0;

    this.availableStock = useMappings ? attributeSelection.AvailableStockCount : this.marketplaceItem.AvailabilityCount;
    this.stockCount = useMappings ? attributeSelection.StockCount : this.marketplaceItem.StockCount;

    const purchaseQuantity = attributeSelection.AvailableStockCount > 0 ? 1 : 0;

    if (this.marketplaceActionsComponent) {
      this.marketplaceActionsComponent.updatePurchaseData(purchaseQuantity, this.attributeMappingID);
    }
  }

  private _updateTheme(marketplaceItem: MarketplaceItemExtended) {
    if (marketplaceItem.Status === MarketplaceItemStatus.Available) {
      this.theme = marketplaceItem.theme;
    } else {
      this.theme = 'muted';
    }
  }

  private _setUpLocalVariables(item: MarketplaceItemExtended) {
    this.marketplaceItem = item;
    //maintain original item status
    this.originalStatus = item.Status;
    this.availableStock = item.AvailabilityCount;
    this.stockCount = item.StockCount;
    this.marketplaceItemType = this.marketplaceExtendedQuery.getMarketplaceItemType(item);
  }

  private _subToUserChange() {
    this.memberQuery.userContext$
      .pipe(
        takeUntil(this._destroyed$),
        map((user) => {
          if (user && user.lcid) {
            this.currentLcid = Number(user.lcid);
          }
        }),
        tap(() => {
          if (this.marketplaceActionsComponent) {
            this.marketplaceActionsComponent.updatePurchaseData();
          }
        })
      )
      .subscribe();
  }

  setCustomFields(fields: MarketplaceBasketCustomFieldModel[] | undefined) {
    this.validCustomFields = fields;
    if (this.marketplaceActionsComponent) {
      this.marketplaceActionsComponent.updateCustomFields(fields);
    }
  }

  /** Will navigate to the back as soon as item's end time reached */
  marketplaceEnd() {
    if (this.isOwnWindow) {
      this.router.navigate([this.completeRedirect]);
    }
  }

  updateLoading(state: boolean) {
    this.loading = state;
  }

  private _setUpTermsAndConditions(item: MarketplaceItemExtended) {
    if (item.TermsAndConditionsType === TermsAndCondtionsType.Text) {
      this.termsLink = this.router.url + '/terms';
    } else {
      this.termsLink = item.TermsAndConditions;
    }
  }

  getStockAvailableState() {
    [this.stockAvailableState, this.textColor] = this.handleStockAvailable(this.marketplaceItem);
  }

  private handleStockAvailable(marketplaceItem: MarketplaceItemExtended) {
    if (!marketplaceItem) return ['', this.theme];
    const stockCount = marketplaceItem.AvailabilityCount;
    // Limited stock
    if (stockCount > 5 && stockCount <= 10) return [MARKETPLACE_STATE.LIMITED, TEXT_COLOR.RED];

    // Stock count left
    if (stockCount > 0 && stockCount <= 5) return [MARKETPLACE_STATE.REMAINING, TEXT_COLOR.RED];
    else return ['', this.theme];
  }
}
